import { InventoryOwnerType } from "@1games-ru/region-shared/dist/enums/inventory/inventory";
import { EquipmentSlot, QuickSlot } from "@1games-ru/region-shared/dist/types/inventory/inventory";
import { useEffect, useMemo } from "react";
import useAppSelector from "@/core/helpers/useAppSelector";
import { EquipSlot } from "@/domains/inventory/constants/equipSlot";
import { OwnerType } from "@/domains/inventory/constants/ownerType";
import {
  equipmentSelector,
  inventoryMapSelector,
  quickSlotsSelector,
} from "@/domains/inventory/slice";
import useInventoryManager from "@/domains/inventory/hooks/inventory/useInventoryManager";
import useContextMenu from "@/domains/inventory/hooks/useContextMenu";
import useGetInventory from "@/domains/inventory/hooks/useGetInventory";
import InventoryContextMenu from "@/domains/inventory/components/contextMenu/InventoryContextMenu";
import InventorySlotCell from "@/domains/inventory/components/dndkit/InventorySlotCell";
import InventorySlotItem from "@/domains/inventory/components/dndkit/InventorySlotItem";

type Props = { onRefresh: () => void };

export default function InventoryCharacterSlotsContainer({ onRefresh }: Props) {
  const { handleContextMenu, isOpen, menuRef, position, itemForContextMenu, closeContextMenu } =
    useContextMenu();
  const { getInventory } = useGetInventory();
  const equipment = useAppSelector(equipmentSelector);
  const quickSlots = useAppSelector(quickSlotsSelector);
  const inventoryMap = useAppSelector(inventoryMapSelector);
  const { equipItem, unEquipItem, assignQuickSlotItem, unassignQuickSlotItem } =
    useInventoryManager();

  const equipmentSlotsMap = useMemo(() => {
    const map: Map<EquipSlot, EquipmentSlot> = new Map();

    if (equipment) {
      Object.values(EquipSlot).forEach((slot) => {
        const finded = equipment?.slots.find((item) => item.slot === slot);
        map.set(slot, finded);
      });
    }

    return map;
  }, [equipment]);

  const renderEquipSlot = (slot: EquipSlot) => {
    const slotData = equipmentSlotsMap.get(slot);
    return (
      <InventorySlotCell
        equipmentId={equipment?.id}
        slot={slot}
        key={slot}
        onEquip={equipItem}
        onRefresh={onRefresh}
      >
        {slotData?.item && (
          <InventorySlotItem
            equipmentId={equipment?.id}
            item={slotData.item}
            slot={slot}
            onRefersh={onRefresh}
            onUnEquip={unEquipItem}
          />
        )}
      </InventorySlotCell>
    );
  };

  const quickSlotsMap = useMemo(() => {
    const map: Map<number, QuickSlot> = new Map(
      (quickSlots || []).filter((item) => item).map((item) => [item.slotNumber, item]),
    );

    return map;
  }, [quickSlots]);

  const renderQuickSlot = (item: QuickSlot | null, quickSlot: number) => {
    if (!item)
      return (
        <InventorySlotCell
          quickSlot={quickSlot}
          key={-quickSlot}
          onAssignQuickSlot={assignQuickSlotItem}
          onRefresh={onRefresh}
        />
      );

    const slotData = quickSlotsMap.get(item.slotNumber);
    return (
      <InventorySlotCell
        key={item.slotNumber}
        quickSlot={item.slotNumber}
        onAssignQuickSlot={assignQuickSlotItem}
        onRefresh={onRefresh}
      >
        {slotData?.inventoryItem && (
          <InventorySlotItem
            item={slotData.inventoryItem}
            quickSlot={quickSlot}
            onRefersh={onRefresh}
            onUnassignQuickSlot={unassignQuickSlotItem}
          />
        )}
      </InventorySlotCell>
    );
  };

  const clothesSlots = useMemo(
    () => [EquipSlot.Hat, EquipSlot.Tops, EquipSlot.Legs, EquipSlot.Shoes],
    [],
  );
  const accessoriesSlots = useMemo(
    () => [
      EquipSlot.Glasses,
      EquipSlot.Mask,
      EquipSlot.Ears,
      EquipSlot.Accessories,
      EquipSlot.Watches,
      EquipSlot.Bracelets,
    ],
    [],
  );

  useEffect(() => {
    if (equipmentSlotsMap.get(EquipSlot.Bags)?.item) {
      const bagInventory = Object.values(inventoryMap).find(
        (item) => item.ownerType === OwnerType.Bag,
      );

      if (!bagInventory) {
        getInventory({
          inventoryOwnerId: equipmentSlotsMap.get(EquipSlot.Bags).item.id,
          inventoryOwnerType: InventoryOwnerType.Bag,
        });
      }
    }
  }, [equipmentSlotsMap.get(EquipSlot.Bags)?.item]);

  const hands = (
    <InventorySlotCell
      equipmentId={equipment.id}
      slot={EquipSlot.Hands}
      onEquip={equipItem}
      onRefresh={onRefresh}
      className="w-[13rem] h-[9.375rem]"
      classNameIcon="h-[2.5rem]"
      label="Активный предмет"
      classNameLabel="text-14 text-white/50"
    >
      {equipmentSlotsMap.get(EquipSlot.Hands)?.item && (
        <InventorySlotItem
          equipmentId={equipment.id}
          item={equipmentSlotsMap.get(EquipSlot.Hands).item}
          slot={EquipSlot.Hands}
          onRefersh={onRefresh}
          onUnEquip={unEquipItem}
          onContextMenu={handleContextMenu}
        />
      )}
    </InventorySlotCell>
  );

  const bodyArmor = (
    <InventorySlotCell
      equipmentId={equipment.id}
      slot={EquipSlot.BodyArmors}
      onEquip={equipItem}
      onRefresh={onRefresh}
      className="w-[7rem] h-[9.375rem]"
      classNameIcon="h-[2.5rem]"
      label="Бронежилет"
      classNameLabel="text-14 text-white/50"
    >
      {equipmentSlotsMap.get(EquipSlot.BodyArmors)?.item && (
        <InventorySlotItem
          equipmentId={equipment.id}
          item={equipmentSlotsMap.get(EquipSlot.BodyArmors).item}
          slot={EquipSlot.BodyArmors}
          onRefersh={onRefresh}
          onUnEquip={unEquipItem}
          onContextMenu={handleContextMenu}
        />
      )}
    </InventorySlotCell>
  );

  const bags = (
    <InventorySlotCell
      equipmentId={equipment.id}
      slot={EquipSlot.Bags}
      onEquip={equipItem}
      onRefresh={onRefresh}
      className="w-[7rem] h-[9.375rem]"
      classNameIcon="h-[2.5rem]"
      label="Рюкзак"
      classNameLabel="text-14 text-white/50"
    >
      {equipmentSlotsMap.get(EquipSlot.Bags)?.item && (
        <InventorySlotItem
          equipmentId={equipment.id}
          item={equipmentSlotsMap.get(EquipSlot.Bags).item}
          slot={EquipSlot.Bags}
          onRefersh={onRefresh}
          onUnEquip={(_, __, item) => {
            getInventory({
              inventoryOwnerId: item.id,
              inventoryOwnerType: InventoryOwnerType.Bag,
            });
          }}
          onContextMenu={handleContextMenu}
        />
      )}
    </InventorySlotCell>
  );

  return (
    <>
      <div className="flex items-start justify-between">
        <div className="flex flex-col items-start gap-3">
          <div className="text-15 font-medium text-white/60">Одежда</div>
          <div className="grid grid-cols-3 gap-2.5">
            {clothesSlots.map((clotheSlot) => renderEquipSlot(clotheSlot))}
          </div>
        </div>
        <div className="flex flex-col items-end gap-3">
          <div className="text-15 font-medium text-white/60">Акссесуары</div>
          <div className="grid grid-cols-3 gap-2.5">
            {accessoriesSlots.map((accessorySlot) => renderEquipSlot(accessorySlot))}
          </div>
        </div>
      </div>
      <div className="flex flex-col gap-3 w-full">
        <div className="flex items-center justify-between w-full">
          <div className="text-15 font-medium text-white/60">Быстрые слоты</div>
          <div className="text-15 font-medium text-white/60">Экипировка</div>
        </div>
        <div className="flex items-center justify-between w-full gap-3.5">
          <div className="grid grid-cols-3 gap-2.5">
            {quickSlots.map((item, index) => renderQuickSlot(item, index + 1))}
          </div>
          <div className="flex items-center gap-3.5">
            {hands}
            {bodyArmor}
            {bags}
          </div>
        </div>
      </div>
      {itemForContextMenu && isOpen && (
        <InventoryContextMenu
          ref={menuRef}
          equipmentId={equipment.id}
          position={position}
          item={itemForContextMenu}
          onUnEquip={unEquipItem}
          onClose={closeContextMenu}
          onRefresh={onRefresh}
        />
      )}
    </>
  );
}
